import React, { useState, useEffect } from "react";
import { useDebounce } from "use-debounce";
import axios from "axios";
import * as XLSX from "xlsx";
import { withStyles } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import MuiDialogContent from "@material-ui/core/DialogContent";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Typography from "@material-ui/core/Typography";
import Styles from "./Components.module.scss";
import { historyModalColumns } from "../../../Utils/ColumnTemplate";
import LucidDropdown from "./LucidDropdown";
import {
  X,
  Search,
  Filter,
  Download,
  Clock,
  Users,
  TrendingUp,
} from "lucide-react";
import { DateRangePicker } from "./DateRangePicker";

const styles = (theme) => ({
  root: {
    margin: 24,
    padding: theme.spacing(2),
    fontSize: "22px",
    fontStyle: "normal",
    fontWeight: 600,
    lineHeight: "normal",
  },
  closeButton: {
    position: "absolute",
    right: theme.spacing(4),
    top: theme.spacing(4),
    color: "#000000",
    padding: "0",
  },
});

const DialogTitle = withStyles(styles)((props) => {
  const { children, classes, onClose, ...other } = props;
  return (
    <MuiDialogTitle disableTypography className={classes.root} {...other}>
      <Typography variant="h6">{children}</Typography>
      {onClose ? (
        <IconButton
          aria-label="close"
          className={`${classes.closeButton}`}
          onClick={onClose}
        >
          <CloseIcon />
        </IconButton>
      ) : null}
    </MuiDialogTitle>
  );
});

const DialogContent = withStyles((theme) => ({
  root: {
    padding: "0 24px 24px 24px",
    borderBottom: "unset",
  },
}))(MuiDialogContent);

function HistoryPagination({
  data,
  startIndex,
  endIndex,
  currentPage,
  totalPages,
  totalElements,
  setCurrentPage,
  Styles,
}) {
  return (
    <div className={Styles.historyPagination}>
      <p className={Styles.historyPaginationInfo}>
        Showing {data.length === 0 ? 0 : startIndex + 1} to {endIndex} of{" "}
        {totalElements} calls
      </p>
      <div className={Styles.historyPaginationControls}>
        <button
          onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
          disabled={currentPage === 1}
          className={`${Styles.historyPaginationButton} ${
            currentPage === 1 ? Styles.historyPaginationButtonDisabled : ""
          }`}
        >
          <span className={Styles.historyPaginationButtonLabel}>Previous</span>
        </button>
        {(() => {
          const startPage = Math.min(
            Math.max(1, currentPage),
            Math.max(1, totalPages - 1)
          );
          return Array.from(
            { length: Math.min(2, totalPages) },
            (_, i) => startPage + i
          ).map((pageNum) => (
            <button
              key={pageNum}
              onClick={() => setCurrentPage(pageNum)}
              className={`${Styles.historyPageNumberButton} ${
                currentPage === pageNum
                  ? Styles.historyPageNumberButtonActive
                  : ""
              }`}
            >
              <span
                className={`${Styles.historyPageNumberLabel} ${
                  currentPage === pageNum
                    ? Styles.historyPageNumberLabelActive
                    : Styles.historyPageNumberLabelInactive
                }`}
              >
                {pageNum}
              </span>
            </button>
          ));
        })()}
        <button
          onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
          disabled={currentPage === totalPages || totalPages === 0}
          className={`${Styles.historyPaginationButton} ${
            currentPage === totalPages || totalPages === 0
              ? Styles.historyPaginationButtonDisabled
              : ""
          }`}
        >
          <span className={Styles.historyPaginationButtonLabel}>Next</span>
        </button>
      </div>
    </div>
  );
}

function MetricCard({ title, icon, iconBgColor, value, subtext }) {
  return (
    <div className={Styles.historyMetricCard}>
      <div className={Styles.historyMetricHeader}>
        <span className={Styles.historyMetricTitle}>{title}</span>
        <div
          className={Styles.historyMetricIcon}
          style={{ backgroundColor: iconBgColor }}
        >
          {icon}
        </div>
      </div>
      <p className={Styles.historyMetricValue}>{value ?? "-"}</p>
      {subtext && <p className={Styles.historyMetricSubtext}>{subtext}</p>}
    </div>
  );
}

function HistoryTable({ data, getCallStatusLabel, getStatusStyle, Styles }) {
  const columns = historyModalColumns(
    getCallStatusLabel,
    getStatusStyle,
    Styles
  );

  return (
    <div className={Styles.historyTableOuter}>
      <table className={Styles.historyTable}>
        <thead>
          <tr className={Styles.historyTableHeaderRow}>
            {columns.map((col) => (
              <th
                key={col.accessor}
                className={`${Styles.historyTableHeaderCell}`}
              >
                {col.Header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={Styles.historyEmptyState}>
                <p>No calls found matching your filters</p>
              </td>
            </tr>
          ) : (
            data?.map((call) => (
              <tr key={call.userId} className={Styles.historyTableBodyRow}>
                {columns.map((col) => (
                  <td
                    key={col.accessor}
                    className={`${Styles.historyTableCell}`}
                  >
                    {col.Cell({
                      value: call[col.accessor],
                      row: { original: call },
                    })}
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

export default function HistoryModal(props) {
  const { open, setOpen, title = "", url } = props;

  const [searchQuery, setSearchQuery] = useState("");
  const [showFilters, setShowFilters] = useState(false);
  const [currentStatusFilter, setCurrentStatusFilter] = useState("");
  const [callAttemptsFilter, setCallAttemptsFilter] = useState("");
  const [callAttemptsInput, setCallAttemptsInput] = useState("");
  const [stats, setStats] = useState(null);
  const [data, setData] = useState([]);
  const [showCurrentStatus, setShowCurrentStatus] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [totalPages, setTotalPages] = useState(0);
  const [totalElements, setTotalElements] = useState(0);
  const [isExporting, setIsExporting] = useState(false);
  const itemsPerPage = 8;

  const callStatusMap = {
    2: "Accepted",
    3: "Rejected",
    4: "DNR",
    5: "Incomplete",
  };

  const fetchHistoryData = () => {
    if (url) {
      const queryParams = new URLSearchParams({
        rows: itemsPerPage,
        page_no: currentPage - 1,
      });
      if (searchQuery) queryParams.set("search_by", searchQuery);
      if (callAttemptsFilter)
        queryParams.set("call_attempts", callAttemptsFilter);
      if (currentStatusFilter) {
        const callStatusKey = Object.keys(callStatusMap).find(
          (key) => callStatusMap[key] === currentStatusFilter
        );
        if (callStatusKey) queryParams.set("callStatus", callStatusKey);
      }
      if (dateFrom) queryParams.set("from_date", dateFrom);
      if (dateTo) queryParams.set("to_date", dateTo);

      axios.post(`${url}&${queryParams.toString()}`).then((response) => {
        if (response?.data?.response) {
          let res = response.data.response;
          setStats(res.callDetails);
          setData(res.leadDetails || []);
          setTotalPages(res.totalPages);
          setTotalElements(res.totalElements);
        }
      });
    }
  };

  const [debouncedSearchQuery] = useDebounce(searchQuery, 400);

  useEffect(() => {
    fetchHistoryData();
  }, [
    url,
    currentPage,
    currentStatusFilter,
    callAttemptsFilter,
    dateFrom,
    dateTo,
  ]);

  useEffect(() => {
    fetchHistoryData();
  }, [debouncedSearchQuery]);

  const getCallStatusLabel = (status) => callStatusMap[status] || "-";

  const currentStatusOptions = Object.values(callStatusMap);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;

  const metricCards = [
    {
      title: "Total Mins",
      icon: (
        <Clock
          className={`${Styles.iconLg} ${Styles.iconColorAmber}`}
          strokeWidth={2}
        />
      ),
      iconBgColor: "#fef9c2",
      value: stats?.totalMinutes,
      subtext: "Connected calls",
    },
    {
      title: "Total Leads",
      icon: (
        <Users
          className={`${Styles.iconLg} ${Styles.iconColorPink}`}
          strokeWidth={2}
        />
      ),
      iconBgColor: "#fce7f3",
      value: stats?.totalLeadsCalled,
      subtext: "This month",
    },
    {
      title: "Converted",
      icon: (
        <TrendingUp
          className={`${Styles.iconLg} ${Styles.iconColorGreen}`}
          strokeWidth={2}
        />
      ),
      iconBgColor: "#d1fae5",
      value: stats?.totalConverted,
    },
  ];

  const getStatusStyle = (status) => {
    switch (status) {
      case "Leads to Process":
        return Styles.statusLeadsToProcess;
      case "Accepted":
        return Styles.statusAccepted;
      case "Rejected":
        return Styles.statusRejected;
      case "DNR":
        return Styles.statusDNR;
      case "Incomplete":
        return Styles.statusIncomplete;
      default:
        return Styles.statusDefault;
    }
  };

  const handleClearFilters = () => {
    setCurrentStatusFilter("");
    setCallAttemptsFilter("");
    setCallAttemptsInput("");
    setSearchQuery("");
    setDateFrom("");
    setDateTo("");
  };

  const handleExport = () => {
    if (!url) return;
    setIsExporting(true);

    const queryParams = new URLSearchParams({
      export: 1,
    });
    if (searchQuery) queryParams.set("search_by", searchQuery);
    if (callAttemptsFilter)
      queryParams.set("call_attempts", callAttemptsFilter);
    if (currentStatusFilter) {
      const callStatusKey = Object.keys(callStatusMap).find(
        (key) => callStatusMap[key] === currentStatusFilter
      );
      if (callStatusKey) queryParams.set("callStatus", callStatusKey);
    }
    if (dateFrom) queryParams.set("from_date", dateFrom);
    if (dateTo) queryParams.set("to_date", dateTo);

    axios
      .post(`${url}&${queryParams.toString()}`)
      .then((response) => {
        const rows = response?.data?.response?.leadDetails || [];
        if (!rows?.length) return;

        const formattedRows = rows.map(
          ({ callAttempts, callStatus, calledOnDate, name, phoneNumber }) => ({
            Name: name,
            "Phone Number": phoneNumber,
            "Call Status": callStatusMap[callStatus] || callStatus,
            "Call Attempts": callAttempts,
            "Called On": calledOnDate
              ? new Date(calledOnDate).toLocaleDateString("en-IN")
              : "-",
          })
        );

        const worksheet = XLSX.utils.json_to_sheet(formattedRows);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "History");
        XLSX.writeFile(workbook, "Voice bot history.xlsx");
      })
      .finally(() => setIsExporting(false));
  };

  const handleClose = () => {
    setOpen(false);
  };

  return (
    <Dialog
      onClose={handleClose}
      aria-labelledby="history-dialog-title"
      open={open}
      classes={{ paper: Styles.historyModalPaper }}
      fullWidth
      maxWidth="lg"
    >
      <DialogTitle id="history-dialog-title" onClose={handleClose}>
        {title}
      </DialogTitle>
      <DialogContent>
        <div className={Styles.historyModalContainer}>
          {/* Header Row - History Title with CTAs */}
          <div className={Styles.historyHeaderRow}>
            <h1 className={Styles.historyTitle}>History</h1>

            <div className={Styles.historyHeaderActions}>
              <DateRangePicker
                dateFrom={dateFrom}
                dateTo={dateTo}
                onDateChange={(from, to) => {
                  setDateFrom(from);
                  setDateTo(to);
                  setCurrentPage(1);
                }}
              />

              {/* Filter Button */}
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`${Styles.historyFilterButton} ${
                  showFilters ? Styles.historyFilterButtonActive : ""
                }`}
              >
                <Filter className={Styles.iconMd} strokeWidth={1.33} />
                <span className={Styles.historyFilterButtonLabel}>Filter</span>
              </button>

              {/* Export Button */}
              {!!data.length && (
                <button
                  onClick={handleExport}
                  disabled={isExporting}
                  className={Styles.historyExportButton}
                >
                  <Download
                    className={`${Styles.iconMd} ${Styles.iconColorWhite}`}
                    strokeWidth={1.33}
                  />
                  <span className={Styles.historyExportLabel}>
                    {isExporting ? "Exporting..." : "Export"}
                  </span>
                </button>
              )}
            </div>
          </div>

          {/* Metric Tiles */}
          <div className={Styles.historyMetrics}>
            {metricCards?.map((metric) => (
              <MetricCard key={metric.title} {...metric} />
            ))}
          </div>

          {/* Main Container */}
          <div className={Styles.historyMainContainer}>
            {/* Search Bar */}
            <div className={Styles.historySearchBarWrapper}>
              <Search className={Styles.historySearchIcon} strokeWidth={1.5} />
              <input
                type="text"
                placeholder="Search by name, phone number, or assistant..."
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value);
                  setCurrentPage(1);
                }}
                className={Styles.historySearchInput}
              />
            </div>

            {/* Filter Panel */}
            {showFilters && (
              <div className={Styles.historyFilters}>
                <div className={Styles.historyFiltersHeader}>
                  <h3 className={Styles.historyFiltersTitle}>
                    Advanced Filters
                  </h3>
                  {(currentStatusFilter ||
                    callAttemptsFilter ||
                    searchQuery ||
                    dateFrom ||
                    dateTo) && (
                    <button
                      onClick={handleClearFilters}
                      className={Styles.historyClearAllButton}
                    >
                      Clear All
                    </button>
                  )}
                </div>

                <div className={Styles.historyFiltersGrid}>
                  {/* Current Status */}
                  <div className={Styles.historyFilterField}>
                    <label className={Styles.historyFilterLabel}>
                      Current Status
                    </label>
                    <LucidDropdown
                      showDropdown={showCurrentStatus}
                      setShowDropdown={setShowCurrentStatus}
                      selectedValue={currentStatusFilter}
                      setSelectedValue={setCurrentStatusFilter}
                      setCurrentPage={setCurrentPage}
                      options={currentStatusOptions}
                      placeholder="Select status"
                      allLabel="All Status"
                    />
                  </div>

                  {/* Call Attempts */}
                  <div className={Styles.historyFilterField}>
                    <label className={Styles.historyFilterLabel}>
                      Call Attempts
                    </label>
                    <input
                      type="text"
                      value={callAttemptsInput}
                      placeholder="Enter attempts"
                      className={Styles.lucidInput}
                      onChange={(e) => {
                        const val = e.target.value;
                        if (val === "" || /^\d+$/.test(val)) {
                          setCallAttemptsInput(val);
                        }
                      }}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setCallAttemptsFilter(callAttemptsInput);
                          setCurrentPage(1);
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Active Filters Display */}
                {(() => {
                  const FilterChip = ({ label, onClear }) => (
                    <div className={Styles.historyFilterChip}>
                      <span className={Styles.historyFilterChipLabel}>
                        {label}
                      </span>
                      <button
                        onClick={onClear}
                        className={Styles.historyChipCloseButton}
                      >
                        <X
                          className={`${Styles.iconSm} ${Styles.iconColorNavy}`}
                          strokeWidth={2}
                        />
                      </button>
                    </div>
                  );

                  const activeChips = [
                    searchQuery && {
                      label: `Search: ${searchQuery}`,
                      onClear: () => setSearchQuery(""),
                    },
                    currentStatusFilter && {
                      label: `Current: ${currentStatusFilter}`,
                      onClear: () => setCurrentStatusFilter(""),
                    },
                    callAttemptsFilter && {
                      label: `Attempts: ${callAttemptsFilter}`,
                      onClear: () => {
                        setCallAttemptsFilter("");
                        setCallAttemptsInput("");
                      },
                    },
                    (dateFrom || dateTo) && {
                      label: "Date Range",
                      onClear: () => {
                        setDateFrom("");
                        setDateTo("");
                      },
                    },
                  ].filter(Boolean);

                  return activeChips.length > 0 ? (
                    <div className={Styles.historyActiveFilters}>
                      <span className={Styles.historyActiveFiltersLabel}>
                        Active Filters:
                      </span>
                      {activeChips.map((chip) => (
                        <FilterChip key={chip.label} {...chip} />
                      ))}
                    </div>
                  ) : null;
                })()}
              </div>
            )}

            {/* Table */}
            <HistoryTable
              data={data}
              getCallStatusLabel={getCallStatusLabel}
              getStatusStyle={getStatusStyle}
              Styles={Styles}
            />

            {/* Pagination */}
            {!!data.length && (
              <HistoryPagination
                data={data}
                startIndex={startIndex}
                endIndex={endIndex}
                currentPage={currentPage}
                totalPages={totalPages}
                totalElements={totalElements}
                setCurrentPage={setCurrentPage}
                Styles={Styles}
              />
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
